import React, { useState } from 'react';
import {
  Clock,
  ChevronDown,
  Layers,
  Sparkles,
  Footprints,
  Umbrella,
  Sun,
  Sunset,
  Moon,
  Utensils,
} from 'lucide-react';
import { DayMoment, DAY_MOMENTS, DayMomentInfo } from '../services/momentEngine';
import { Language } from '../types/spot';

export type SmartFilterMode = 'all' | 'now_perfect' | 'minimal_walking' | 'indoor';

interface SmartMomentBarProps {
  currentMoment: DayMoment;
  customHour: number | null;
  onSelectCustomHour: (hour: number | null) => void;
  smartFilter: SmartFilterMode;
  onSelectSmartFilter: (filter: SmartFilterMode) => void;
  language: Language;
  perfectSpotsCount: number;
  walkingSpotsCount: number;
}

export const SmartMomentBar: React.FC<SmartMomentBarProps> = ({
  currentMoment,
  customHour,
  onSelectCustomHour,
  smartFilter,
  onSelectSmartFilter,
  language,
  perfectSpotsCount,
  walkingSpotsCount,
}) => {
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const momentInfo: DayMomentInfo = DAY_MOMENTS[currentMoment];

  const t = {
    de: {
      timeLabel: 'Uhrzeit',
      simulateLabel: 'Tageszeit wählen',
      allPill: 'Alle Empfehlungen',
      allPillShort: 'Alle',
      perfectPill: `Jetzt ideal (${perfectSpotsCount})`,
      perfectPillShort: `Jetzt (${perfectSpotsCount})`,
      perfectTitle: 'Orte, die zu dieser Tageszeit am schönsten sind',
      walkingPill: `Gehweite < 500m (${walkingSpotsCount})`,
      walkingPillShort: `< 500m (${walkingSpotsCount})`,
      walkingTitle: 'Orte in unmittelbarer Fußnähe (< 500 m)',
      indoorPill: 'Indoor & wetterfest',
      indoorPillShort: 'Indoor',
      indoorTitle: 'Malls, Museen und überdachte Highlights',
      momentLive: 'Echtzeit (Live)',
      momentMorning: '09:00 Vormittag (Paläste & Parks)',
      momentLunch: '12:30 Mittag (Food & Märkte)',
      momentAfternoon: '15:00 Nachmittag (Shopping & Cafés)',
      momentSunset: '18:30 Sonnenuntergang & Aussicht',
      momentNight: '21:00 Nacht & Lichter',
    },
    en: {
      timeLabel: 'Time',
      simulateLabel: 'Select Time of Day',
      allPill: 'All Spots',
      allPillShort: 'All',
      perfectPill: `Ideal Now (${perfectSpotsCount})`,
      perfectPillShort: `Now (${perfectSpotsCount})`,
      perfectTitle: 'Spots best visited at this time of day',
      walkingPill: `Walking < 500m (${walkingSpotsCount})`,
      walkingPillShort: `< 500m (${walkingSpotsCount})`,
      walkingTitle: 'Spots within walking distance (< 500 m)',
      indoorPill: 'Indoor & Sheltered',
      indoorPillShort: 'Indoor',
      indoorTitle: 'Malls, museums, and covered highlights',
      momentLive: 'Realtime (Live)',
      momentMorning: '09:00 Morning (Palaces & Parks)',
      momentLunch: '12:30 Lunch (Food & Markets)',
      momentAfternoon: '15:00 Afternoon (Shopping & Cafes)',
      momentSunset: '18:30 Golden Hour & Sunset',
      momentNight: '21:00 Night & Skyline Lights',
    },
    kr: {
      timeLabel: '시간대',
      simulateLabel: '시간대 선택',
      allPill: '전체 추천',
      allPillShort: '전체',
      perfectPill: `지금 최적 (${perfectSpotsCount})`,
      perfectPillShort: `지금 (${perfectSpotsCount})`,
      perfectTitle: '현재 시간대에 가장 방문하기 좋은 장소',
      walkingPill: `도보 < 500m (${walkingSpotsCount})`,
      walkingPillShort: `< 500m (${walkingSpotsCount})`,
      walkingTitle: '500m 이내 가까운 거리',
      indoorPill: '실내 & 우천 추천',
      indoorPillShort: '실내',
      indoorTitle: '몰, 박물관 등 날씨 영향 없는 장소',
      momentLive: '실시간',
      momentMorning: '09:00 오전 (궁궐 & 공원)',
      momentLunch: '12:30 점심 (맛집 & 시장)',
      momentAfternoon: '15:00 오후 (쇼핑 & 카페)',
      momentSunset: '18:30 일몰 & 골든아워',
      momentNight: '21:00 야경 명소',
    },
  }[language];

  const momentOptions: {
    id: DayMoment | 'live';
    label: string;
    hour: number | null;
    icon: React.ElementType;
  }[] = [
    { id: 'live', label: t.momentLive, hour: null, icon: Clock },
    { id: 'morning', label: t.momentMorning, hour: 9.5, icon: Sun },
    { id: 'lunch', label: t.momentLunch, hour: 12.5, icon: Utensils },
    { id: 'afternoon', label: t.momentAfternoon, hour: 15.0, icon: Sun },
    { id: 'sunset', label: t.momentSunset, hour: 18.5, icon: Sunset },
    { id: 'night', label: t.momentNight, hour: 21.0, icon: Moon },
  ];

  const momentLabel =
    language === 'de'
      ? momentInfo.labelDe
      : language === 'kr'
      ? momentInfo.labelKr
      : momentInfo.labelEn;
  const momentTip =
    language === 'de'
      ? momentInfo.vibeTipDe
      : language === 'kr'
      ? momentInfo.vibeTipKr
      : momentInfo.vibeTipEn;

  return (
    <div className="smart-moment-bar">
      {/* Top Bar: Current Moment Banner & Time Changer */}
      <div className="smart-moment-bar__header">
        {/* Left: Moment Info Badge */}
        <div className="smart-moment-bar__badge">
          <div className="smart-moment-bar__title">
            <span className="smart-moment-bar__moment-name">{momentLabel}</span>
            <span className="smart-moment-bar__timerange">
              {momentInfo.timeRange}
            </span>
          </div>
          <div className="smart-moment-bar__tip">{momentTip}</div>
        </div>

        {/* Right: Time Switcher Dropdown */}
        <div className="dropdown">
          <button
            onClick={() => setIsTimeDropdownOpen(!isTimeDropdownOpen)}
            className="btn btn--ghost btn--sm smart-moment-bar__time-btn"
            aria-expanded={isTimeDropdownOpen}
          >
            <Clock style={{ width: 14, height: 14 }} />
            <span>
              {customHour !== null
                ? `${t.timeLabel}: ${momentLabel.split('&')[0].trim()}`
                : t.timeLabel}
            </span>
            <ChevronDown style={{ width: 12, height: 12 }} />
          </button>

          {isTimeDropdownOpen && (
            <>
              <div
                className="dropdown__overlay"
                onClick={() => setIsTimeDropdownOpen(false)}
              />
              <div className="dropdown__menu dropdown__menu--right">
                <div className="dropdown__label">{t.simulateLabel}</div>
                {momentOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      onSelectCustomHour(opt.hour);
                      setIsTimeDropdownOpen(false);
                    }}
                    className={`dropdown__item ${
                      (opt.hour === null && customHour === null) ||
                      (opt.hour !== null && customHour === opt.hour)
                        ? 'dropdown__item--selected'
                        : ''
                    }`}
                  >
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Filter Quick Pills */}
      <div className="smart-moment-bar__filters" role="group" aria-label="Smarte Filter">
        {/* All Filter */}
        <button
          onClick={() => onSelectSmartFilter('all')}
          className={`smart-pill ${
            smartFilter === 'all' ? 'smart-pill--active' : ''
          }`}
        >
          <Layers style={{ width: 14, height: 14 }} />
          <span className="pill__label-full">{t.allPill}</span>
          <span className="pill__label-short">{t.allPillShort}</span>
        </button>

        {/* Now Perfect Filter */}
        <button
          onClick={() => onSelectSmartFilter('now_perfect')}
          className={`smart-pill ${
            smartFilter === 'now_perfect' ? 'smart-pill--active' : ''
          }`}
          title={t.perfectTitle}
        >
          <Sparkles style={{ width: 14, height: 14 }} />
          <span className="pill__label-full">{t.perfectPill}</span>
          <span className="pill__label-short">{t.perfectPillShort}</span>
        </button>

        {/* Minimal Walking Filter */}
        <button
          onClick={() => onSelectSmartFilter('minimal_walking')}
          className={`smart-pill ${
            smartFilter === 'minimal_walking' ? 'smart-pill--active' : ''
          }`}
          title={t.walkingTitle}
        >
          <Footprints style={{ width: 14, height: 14 }} />
          <span className="pill__label-full">{t.walkingPill}</span>
          <span className="pill__label-short">{t.walkingPillShort}</span>
        </button>

        {/* Indoor & Rain Mode Filter */}
        <button
          onClick={() => onSelectSmartFilter('indoor')}
          className={`smart-pill ${
            smartFilter === 'indoor' ? 'smart-pill--active' : ''
          }`}
          title={t.indoorTitle}
        >
          <Umbrella style={{ width: 14, height: 14 }} />
          <span className="pill__label-full">{t.indoorPill}</span>
          <span className="pill__label-short">{t.indoorPillShort}</span>
        </button>
      </div>
    </div>
  );
};
